import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Key } from 'react';
import type { FilterDropdownProps } from 'antd/es/table/interface';

import type { TableProps } from '../types';
import type { MadeColumnType } from './useMakeColumns';
import { TableAction } from '../VirtualTable';

const FILTER_DROPDOWN_MIN_WIDTH = 250;

interface Props {
    buildAllFilters: (overrideKey: string, overrideValue: Key[] | null) => Record<string, Key[] | null>;
    onTableChange?: TableProps<any>['onChange'];
    dataSource: readonly any[];
    resetScroll: () => void;
    tableRef: React.RefObject<HTMLDivElement | null>;
    columns: MadeColumnType[];
}

interface Data {
    filterDropdownOpenKey: string | null;
    setFilterDropdownOpenKey: React.Dispatch<React.SetStateAction<string | null>>;
    filterTriggerRef: React.MutableRefObject<HTMLDivElement | null>;
    renderFilterDropdown: () => React.ReactNode;
}

export function useFilterDropdown({
    buildAllFilters,
    onTableChange,
    dataSource,
    resetScroll,
    tableRef,
    columns,
}: Props): Data {
    const [filterDropdownOpenKey, setFilterDropdownOpenKey] = useState<string | null>(null);
    const [filterPendingKeys, setFilterPendingKeys] = useState<Record<string, Key[]>>({});
    const filterPendingKeysRef = useRef<Record<string, Key[]>>({});
    const filterTriggerRef = useRef<HTMLDivElement | null>(null);
    const filterPortalRef = useRef<HTMLDivElement | null>(null);
    const [filterDropdownPosition, setFilterDropdownPosition] = useState<{
        top: number;
        right?: number;
        left?: number;
    } | null>(null);

    const handleFilterConfirm = useCallback(
        (columnKey: string) => {
            const keys = filterPendingKeysRef.current[columnKey] ?? filterPendingKeys[columnKey] ?? [];
            onTableChange?.(
                {},
                buildAllFilters(columnKey, keys),
                {},
                { action: TableAction.Filter, currentDataSource: dataSource as any[] },
            );
            setFilterDropdownOpenKey(null);
            resetScroll();
        },
        [filterPendingKeys, onTableChange, dataSource, resetScroll, buildAllFilters],
    );

    const getFilterProps = useCallback(
        (column: MadeColumnType, columnKey: string): FilterDropdownProps => {
            const filteredValue = column.filteredValue;
            const selectedKeys: Key[] =
                filterDropdownOpenKey === columnKey
                    ? (filterPendingKeys[columnKey] ?? (Array.isArray(filteredValue) ? (filteredValue as Key[]) : []))
                    : Array.isArray(filteredValue)
                      ? (filteredValue as Key[])
                      : [];

            return {
                prefixCls: 'virtual-table-filter',
                selectedKeys,
                setSelectedKeys: (keys: Key[]) => {
                    filterPendingKeysRef.current = {
                        ...filterPendingKeysRef.current,
                        [columnKey]: keys,
                    };
                    setFilterPendingKeys(prev => ({ ...prev, [columnKey]: keys }));
                },
                confirm: () => handleFilterConfirm(columnKey),
                clearFilters: () => {
                    filterPendingKeysRef.current = {
                        ...filterPendingKeysRef.current,
                        [columnKey]: [],
                    };
                    setFilterPendingKeys(prev => ({ ...prev, [columnKey]: [] }));
                    onTableChange?.(
                        {},
                        buildAllFilters(columnKey, null),
                        {},
                        { action: TableAction.Filter, currentDataSource: dataSource as any[] },
                    );
                    setFilterDropdownOpenKey(null);
                    resetScroll();
                },
                close: () => setFilterDropdownOpenKey(null),
                visible: filterDropdownOpenKey === columnKey,
            };
        },
        [
            filterDropdownOpenKey,
            filterPendingKeys,
            handleFilterConfirm,
            onTableChange,
            dataSource,
            resetScroll,
            buildAllFilters,
        ],
    );

    useEffect(() => {
        if (!filterDropdownOpenKey) {
            setFilterPendingKeys({});
            filterPendingKeysRef.current = {};
        }
    }, [filterDropdownOpenKey]);

    useLayoutEffect(() => {
        if (!filterDropdownOpenKey) {
            setFilterDropdownPosition(null);
            return;
        }
        const el = filterTriggerRef.current;
        const tableEl = tableRef.current;
        if (!el) {
            setFilterDropdownPosition(null);
            return;
        }
        const rect = el.getBoundingClientRect();
        const tableRect = tableEl?.getBoundingClientRect();
        const wouldOverflowTableLeft = tableRect != null && rect.right - FILTER_DROPDOWN_MIN_WIDTH < tableRect.left;
        setFilterDropdownPosition({
            top: rect.bottom + 4,
            ...(wouldOverflowTableLeft ? { left: rect.left } : { right: window.innerWidth - rect.right }),
        });
    }, [filterDropdownOpenKey, tableRef]);

    useEffect(() => {
        if (!filterDropdownOpenKey) return;
        const handleMouseDown = (e: MouseEvent) => {
            const target = e.target as Node;
            if (filterTriggerRef.current?.contains(target) || filterPortalRef.current?.contains(target)) {
                return;
            }
            setFilterDropdownOpenKey(null);
        };
        document.addEventListener('mousedown', handleMouseDown, true);
        return () => document.removeEventListener('mousedown', handleMouseDown, true);
    }, [filterDropdownOpenKey]);

    const renderFilterDropdown = useCallback((): React.ReactNode => {
        if (!filterDropdownOpenKey || !filterDropdownPosition) return null;

        const col = columns.find(c => String(c.key) === filterDropdownOpenKey && c.filterDropdown);
        if (!col) return null;

        const filterContent =
            typeof col.filterDropdown === 'function'
                ? col.filterDropdown(getFilterProps(col, filterDropdownOpenKey))
                : col.filterDropdown;

        return createPortal(
            <div
                key={filterDropdownOpenKey}
                ref={filterPortalRef}
                className="virtual_table_filter_dropdown_portal"
                style={{
                    top: filterDropdownPosition.top,
                    ...(filterDropdownPosition.right !== undefined
                        ? { right: filterDropdownPosition.right }
                        : { left: filterDropdownPosition.left }),
                    zIndex: 10000,
                }}
            >
                {filterContent}
            </div>,
            document.body,
        );
    }, [filterDropdownOpenKey, filterDropdownPosition, columns, getFilterProps]);

    return {
        filterDropdownOpenKey,
        setFilterDropdownOpenKey,
        filterTriggerRef,
        renderFilterDropdown,
    };
}
